import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { vulnerabilitiesApi, Vulnerability, CreateVulnerabilityRequest, UpdateVulnerabilityRequest } from '../services/vulnerabilitiesApi'

interface VulnerabilitiesState {
  vulnerabilities: Vulnerability[]
  pagination: {
    page: number
    limit: number
    total: number
    pages: number
  }
  loading: boolean
  error: string | null
  currentProjectId: string | null
  currentAssetId: string | null
  currentScanId: string | null
}

interface VulnerabilitiesActions {
  fetchVulnerabilities: (params: {
    projectId?: string
    assetId?: string
    scanId?: string
    page?: number
    limit?: number
    search?: string
  }) => Promise<void>
  fetchVulnerabilitiesByTenant: () => Promise<void>
  fetchVulnerabilitiesByAssetId: (assetId: string) => Promise<Vulnerability[]>
  fetchVulnerabilitiesByScanId: (scanId: string) => Promise<Vulnerability[]>
  createVulnerability: (data: CreateVulnerabilityRequest) => Promise<boolean>
  updateVulnerability: (vulnerabilityId: string, data: UpdateVulnerabilityRequest) => Promise<boolean>
  deleteVulnerability: (vulnerabilityId: string) => Promise<boolean>
  fixVulnerability: (vulnerabilityId: string) => Promise<boolean>
  markReadyToRevalidate: (vulnerabilityId: string) => Promise<boolean>
  clearError: () => void
  setCurrentContext: (projectId?: string, assetId?: string, scanId?: string) => void
}

type VulnerabilitiesStore = VulnerabilitiesState & VulnerabilitiesActions

export const useVulnerabilitiesStore = create<VulnerabilitiesStore>()(
  persist(
    (set) => ({
      // State
      vulnerabilities: [],
      pagination: {
        page: 1,
        limit: 10,
        total: 0,
        pages: 0
      },
      loading: false,
      error: null,
      currentProjectId: null,
      currentAssetId: null,
      currentScanId: null,

      // Actions
      fetchVulnerabilities: async (params) => {
        set({ loading: true, error: null })
        
        try {
          const response = await vulnerabilitiesApi.getVulnerabilities(params)
          
          if (response.success && response.data) {
            set({
              vulnerabilities: response.data,
              pagination: {
                page: params.page || 1,
                limit: params.limit || 10,
                total: response.data.length, // This might need adjustment based on actual API response
                pages: Math.ceil(response.data.length / (params.limit || 10))
              },
              loading: false
            })
          } else {
            set({
              error: response.message || 'Failed to fetch vulnerabilities',
              loading: false
            })
          }
        } catch (error: any) {
          set({
            error: error.message || 'Failed to fetch vulnerabilities',
            loading: false
          })
        }
      },

      fetchVulnerabilitiesByTenant: async () => {
        set({ loading: true, error: null })
        
        try {
          // Fetch all vulnerabilities for the tenant (no specific filters)
          const response = await vulnerabilitiesApi.getVulnerabilities({
            limit: 1000 // Get a large number to show all
          })
          
          if (response.success && response.data) {
            set({
              vulnerabilities: response.data,
              loading: false
            })
          } else {
            set({
              error: response.message || 'Failed to fetch vulnerabilities',
              loading: false
            })
          }
        } catch (error: any) {
          set({
            error: error.message || 'Failed to fetch vulnerabilities',
            loading: false
          })
        }
      },

      fetchVulnerabilitiesByAssetId: async (assetId) => {
        set({ loading: true, error: null })
        
        try {
          const response = await vulnerabilitiesApi.getVulnerabilitiesByAssetId(assetId)
          
          if (response.success && response.data) {
            set({ loading: false })
            return response.data
          } else {
            set({
              error: response.message || 'Failed to fetch vulnerabilities',
              loading: false
            })
            return []
          }
        } catch (error: any) {
          set({
            error: error.message || 'Failed to fetch vulnerabilities',
            loading: false
          })
          return []
        }
      },

      fetchVulnerabilitiesByScanId: async (scanId) => {
        set({ loading: true, error: null })
        
        try {
          const response = await vulnerabilitiesApi.getVulnerabilitiesByScanId(scanId)
          
          if (response.success && response.data) {
            set({ loading: false })
            return response.data
          } else {
            set({
              error: response.message || 'Failed to fetch vulnerabilities',
              loading: false
            })
            return []
          }
        } catch (error: any) {
          set({
            error: error.message || 'Failed to fetch vulnerabilities',
            loading: false
          })
          return []
        }
      },

      createVulnerability: async (data) => {
        set({ loading: true, error: null })
        
        try {
          const response = await vulnerabilitiesApi.createVulnerability(data)
          
          if (response.success && response.data) {
            set(state => ({
              vulnerabilities: [...state.vulnerabilities, response.data!],
              loading: false
            }))
            return true
          } else {
            set({
              error: response.message || 'Failed to create vulnerability',
              loading: false
            })
            return false
          }
        } catch (error: any) {
          set({
            error: error.message || 'Failed to create vulnerability',
            loading: false
          })
          return false
        }
      },

      updateVulnerability: async (vulnerabilityId, data) => {
        set({ loading: true, error: null })
        
        try {
          const response = await vulnerabilitiesApi.updateVulnerability(vulnerabilityId, data)
          
          if (response.success && response.data) {
            set(state => ({
              vulnerabilities: state.vulnerabilities.map(vuln => 
                vuln._id === vulnerabilityId ? response.data! : vuln
              ),
              loading: false
            }))
            return true
          } else {
            set({
              error: response.message || 'Failed to update vulnerability',
              loading: false
            })
            return false
          }
        } catch (error: any) {
          set({
            error: error.message || 'Failed to update vulnerability',
            loading: false
          })
          return false
        }
      },

      deleteVulnerability: async (vulnerabilityId) => {
        set({ loading: true, error: null })
        
        try {
          const response = await vulnerabilitiesApi.deleteVulnerability(vulnerabilityId)
          
          if (response.success) {
            set(state => ({
              vulnerabilities: state.vulnerabilities.filter(vuln => vuln._id !== vulnerabilityId),
              loading: false
            }))
            return true
          } else {
            set({
              error: response.message || 'Failed to delete vulnerability',
              loading: false
            })
            return false
          }
        } catch (error: any) {
          set({
            error: error.message || 'Failed to delete vulnerability',
            loading: false
          })
          return false
        }
      },

      fixVulnerability: async (vulnerabilityId: string) => {
        set({ loading: true, error: null })
        
        try {
          const response = await vulnerabilitiesApi.fixVulnerability(vulnerabilityId)
          
          if (response.success) {
            set(state => ({
              vulnerabilities: state.vulnerabilities.map(vuln => 
                vuln._id === vulnerabilityId 
                  ? { ...vuln, status: 'FIXED' }
                  : vuln
              ),
              loading: false
            }))
            return true
          } else {
            set({
              error: response.message || 'Failed to fix vulnerability',
              loading: false
            })
            return false
          }
        } catch (error: any) {
          set({
            error: error.message || 'Failed to fix vulnerability',
            loading: false
          })
          return false
        }
      },

      markReadyToRevalidate: async (vulnerabilityId: string) => {
        set({ loading: true, error: null })
        
        try {
          const response = await vulnerabilitiesApi.markReadyToRevalidate(vulnerabilityId)
          
          if (response.success) {
            set(state => ({
              vulnerabilities: state.vulnerabilities.map(vuln => 
                vuln._id === vulnerabilityId 
                  ? { ...vuln, status: 'READY_TO_REVALIDATE' }
                  : vuln
              ),
              loading: false
            }))
            return true
          } else {
            set({
              error: response.message || 'Failed to mark vulnerability as ready to revalidate',
              loading: false
            })
            return false
          }
        } catch (error: any) {
          set({
            error: error.message || 'Failed to mark vulnerability as ready to revalidate',
            loading: false
          })
          return false
        }
      },

      clearError: () => {
        set({ error: null })
      },

      setCurrentContext: (projectId, assetId, scanId) => {
        set({
          currentProjectId: projectId || null,
          currentAssetId: assetId || null,
          currentScanId: scanId || null
        })
      }
    }),
    {
      name: 'vulnerabilities-store',
      partialize: (state) => ({
        currentProjectId: state.currentProjectId,
        currentAssetId: state.currentAssetId,
        currentScanId: state.currentScanId
      })
    }
  )
)
